import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Bell } from "@phosphor-icons/react";
import { Badge } from "./ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [notiRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications`),
        axios.get(`${API_URL}/api/notifications/unread-count`),
      ]);
      setNotifications(notiRes.data);
      setUnreadCount(countRes.data.count);
    } catch {}
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`);
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  const typeColor = {
    critical: "bg-red-100 text-red-700 border-red-200",
    warning: "bg-amber-100 text-amber-700 border-amber-200",
    info: "bg-indigo-100 text-indigo-700 border-indigo-200",
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors" data-testid="notification-bell">
          <Bell size={22} className="text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold" data-testid="unread-badge">
              {unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-medium text-slate-900">{t("notifications.title")}</h3>
          {unreadCount > 0 && (
            <Button size="sm" variant="ghost" className="text-xs text-indigo-600" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
              {t("notifications.mark_read")}
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell size={24} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">{t("notifications.no_notifications")}</p>
            </div>
          ) : (
            notifications.slice(0, 10).map((n) => (
              <div
                key={n.id}
                className={`px-4 py-3 border-b border-slate-50 ${!n.read ? "bg-indigo-50/30" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.read ? "bg-transparent" : "bg-indigo-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900">{n.title}</p>
                      <Badge className={`text-xs ${typeColor[n.type] || typeColor.info}`}>{n.type}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
